import type {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';

import { AlertService } from '@core/service/alert/alert.service';
import { AuthState, Logout, RefreshToken } from '@data/state';

// 401/400 here means bad credentials/token, not an expired session - let the
// component handle it inline instead of triggering a refresh or logout.
const UNAUTHENTICATED_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/confirm-email',
];

// Module-scoped so concurrent 401s coalesce onto one refresh call, not one each.
const REFRESH_FAILED = Symbol('refresh-failed');
type RefreshSignal = string | null | typeof REFRESH_FAILED;
let isRefreshing = false;
const refreshedToken$ = new BehaviorSubject<RefreshSignal>(null);

const addToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
  request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

export const baseHttpInterceptor: HttpInterceptorFn = (request, next) => {
  const store = inject(Store);
  const router = inject(Router);
  const alertService = inject(AlertService);

  const token = store.selectSnapshot(AuthState.token);
  const authedRequest = token ? addToken(request, token) : request;

  const isUnauthenticatedAuthEndpoint = UNAUTHENTICATED_AUTH_ENDPOINTS.some((path) =>
    request.url.includes(path),
  );
  const isRefreshEndpoint = request.url.includes('/auth/refresh');

  return next(authedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isUnauthenticatedAuthEndpoint) {
        return throwError(() => error);
      }

      if (isRefreshEndpoint) {
        // Refresh call itself 401'd - session's dead, not retriable.
        if (error?.status === 401) forceLogout(store, router);
        return throwError(() => error);
      }

      if (error?.status === 401) {
        return handleUnauthorized(authedRequest, next, store, router);
      }

      switch (error?.status) {
        case 403:
          alertService.error('You do not have permission to perform this action.');
          break;
        default:
          if (error?.status >= 500) {
            alertService.error(error.error?.message ?? 'Something went wrong. Please try again.');
          }
          break;
      }
      return throwError(() => error);
    }),
  );
};

function handleUnauthorized(
  request: HttpRequest<unknown>,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  store: Store,
  router: Router,
): Observable<HttpEvent<unknown>> {
  const refreshToken = store.selectSnapshot(AuthState.refreshToken);
  if (!refreshToken) {
    forceLogout(store, router);
    return throwError(() => new Error('Session expired.'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshedToken$.next(null);

    return store.dispatch(new RefreshToken()).pipe(
      switchMap(() => {
        isRefreshing = false;
        const newToken = store.selectSnapshot(AuthState.token);
        refreshedToken$.next(newToken);
        return next(addToken(request, newToken!));
      }),
      catchError((error) => {
        isRefreshing = false;
        refreshedToken$.next(REFRESH_FAILED);
        forceLogout(store, router);
        return throwError(() => error);
      }),
    );
  }

  // A refresh is already in flight - wait for it, then retry with its token.
  // On failure, unblock waiters with an error too, or they'd hang forever.
  return refreshedToken$.pipe(
    filter((signal): signal is string | typeof REFRESH_FAILED => signal !== null),
    take(1),
    switchMap((signal) =>
      signal === REFRESH_FAILED
        ? throwError(() => new Error('Session expired.'))
        : next(addToken(request, signal)),
    ),
  );
}

function forceLogout(store: Store, router: Router): void {
  store.dispatch(new Logout());
  router.navigate(['/auth/login']);
}
